/** Profit Centre → Money: sales & profit + payments in (portal and Xero) with date / business / type filters. */
import React, { useCallback, useEffect, useState } from 'react';
import { Download, Landmark, Loader2, Percent, Plus, PoundSterling, RefreshCw, TrendingUp, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { StatTile } from '@/components/training/analytics/shared';
import { SalesOrderDialog, gbp } from '@/components/sales/SalesOrderDialog';
import { MoneyFilters, PRESETS } from './MoneyFilters';
import { MoneyTable } from './MoneyTable';
import { MonthlyChart, TopProducts } from '@/components/overview/OverviewCards';
import { MonthlyCostsTable } from '@/components/overview/OverviewTables';

const initial = () => { const [start, end] = PRESETS[1].range(); return { preset: 'week', start, end, business: 'all', show: 'all' }; };

export function MoneyTab() {
  const [f, setF] = useState(initial);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [onlyUncosted, setOnlyUncosted] = useState(false);
  const [months, setMonths] = useState(false);

  const [loadError, setLoadError] = useState('');
  const load = useCallback(async () => {
    if (!f.start || !f.end) return;
    setBusy(true); setLoadError('');
    try {
      const qs = new URLSearchParams({ start_date: f.start, end_date: f.end, business: f.business, show: f.show });
      const r = await fetch(`${API_URL}/api/staff/profit-centre/money?${qs}`, { credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Could not load (${r.status})`);
      setData(d);
    } catch (e) { setLoadError(e.message || 'Load failed'); toast.error(e.message); } finally { setBusy(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const remove = async (order) => {
    if (!window.confirm(`Delete the ${gbp(order.sale_gross)} sale for ${order.customer || 'this customer'}?`)) return;
    const r = await fetch(`${API_URL}/api/staff/sales-orders/${order.id}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) { toast.success('Sale deleted'); load(); } else toast.error('Delete failed');
  };
  const syncXero = async () => {
    setSyncing(true);
    try { await fetch(`${API_URL}/api/xero/payments/sync-now`, { method: 'POST', credentials: 'include' }); await load(); toast.success('Checked Xero for new payments'); } catch { toast.error('Xero check failed'); } finally { setSyncing(false); }
  };
  const exportCsv = () => {
    const rows = data?.rows || [];
    const head = ['Date', 'Type', 'Source', 'Business', 'Who', 'What', 'Method', 'Amount', 'Inc VAT', 'Cost', 'Profit'];
    const lines = rows.map((r) => [r.date, r.type, r.source === 'order' ? r.stream_label : r.source, r.business, r.who, r.what, r.method, r.amount, r.amount_gross ?? '', r.cost ?? '', r.profit ?? ''].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv' })); a.download = `money-${f.start}-to-${f.end}.csv`; a.click();
  };

  const t = data?.totals;
  const showSales = f.show === 'all' || f.show === 'sales';
  const showPortal = f.show === 'all' || f.show === 'portal';
  const showXero = f.show === 'all' || f.show === 'xero';
  return (
    <div className="space-y-4" data-testid="pc-money">
      <MoneyFilters f={f} onChange={setF} />
      {loadError && !busy && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 flex flex-wrap items-center gap-2" data-testid="money-load-error">
          <span>Couldn't load this period: {loadError}</span>
          <Button size="sm" variant="outline" onClick={load} className="ml-auto h-7" data-testid="money-retry"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Try again</Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs text-gray-500 flex items-center gap-2">{busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</> : data ? `${data.rows.length} row${data.rows.length === 1 ? '' : 's'}` : ''}
          {showXero && data?.xero && !data.xero.connected && <span className="text-amber-700" data-testid="money-xero-disconnected">· Xero not connected</span>}
          {showXero && data?.xero?.errors?.length > 0 && <span className="text-rose-700" data-testid="money-xero-error">· Xero: {data.xero.errors[0]}</span>}
        </div>
        <div className="ml-auto flex gap-2">
          {showXero && <Button size="sm" variant="outline" onClick={syncXero} disabled={syncing} data-testid="money-xero-sync"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Check Xero</Button>}
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={!data?.rows?.length} data-testid="money-export"><Download className="w-3.5 h-3.5 mr-1" /> CSV</Button>
          <Button size="sm" onClick={() => setDialog({})} className="bg-[#D4A24B] hover:bg-[#c2933f] text-[#0B0B0E] font-bold" data-testid="money-log-sale"><Plus className="w-4 h-4 mr-1" /> Log a sale</Button>
        </div>
      </div>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="money-tiles">
          {showSales && <>
            <StatTile icon={PoundSterling} label="Revenue ex VAT" value={gbp(t.sales_net)} sub={`${t.sales_count} sale${t.sales_count === 1 ? '' : 's'} · orders, courses & logged sales`} testid="money-sales" />
            <StatTile icon={TrendingUp} label="Costs" value={gbp(t.cost)} sub={t.uncosted ? `${t.uncosted} sale${t.uncosted === 1 ? '' : 's'} missing a cost` : 'Order costs + monthly business costs'} tone={t.uncosted ? 'warn' : 'default'} testid="money-cost" />
            <StatTile icon={PoundSterling} label="Profit" value={gbp(t.profit)} tone={t.profit >= 0 ? 'good' : 'bad'} sub="Revenue ex VAT minus all costs" testid="money-profit" />
            <StatTile icon={Percent} label="Margin" value={`${t.margin}%`} sub="Profit ÷ sales" testid="money-margin" />
          </>}
          {showPortal && <StatTile icon={Wallet} label="Received (portal)" value={gbp(t.portal_net)} tone="info" sub={t.portal_refunds ? `${gbp(t.portal_in)} in · ${gbp(t.portal_refunds)} refunds` : 'Course payments, cash log, paid sales'} testid="money-portal" />}
          {showXero && <StatTile icon={Landmark} label="Received (Xero)" value={gbp(t.xero_in)} tone="info" sub={data.xero?.connected ? 'Bank receipts per Xero' : 'Connect Xero in Integrations'} testid="money-xero" />}
        </div>
      )}

      {data?.by_business?.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="money-by-business">
          {data.by_business.map((b) => (
            <button key={b.key} type="button" onClick={() => setF({ ...f, business: b.key })} className="bg-white border rounded-lg px-3 py-2 text-left hover:border-gray-400" data-testid={`money-biz-${b.key}`}>
              <div className="text-xs font-bold text-gray-800">{b.label}</div>
              <div className="text-[11px] text-gray-500">{showSales && b.sales_net ? `Sales ${gbp(b.sales_net)} · profit ${gbp(b.profit)}` : ''}{showSales && b.sales_net && (b.portal_in || b.xero_in) ? ' · ' : ''}{(showPortal || showXero) && (b.portal_in || b.xero_in) ? `In ${gbp((showPortal ? b.portal_in : 0) + (showXero ? b.xero_in : 0))}` : ''}</div>
            </button>
          ))}
        </div>
      )}

      {showSales && data?.overview && (
        <div className="space-y-3" data-testid="money-months">
          <button type="button" onClick={() => setMonths((m) => !m)} className="text-xs font-semibold text-emerald-800 hover:underline" data-testid="money-months-toggle">{months ? 'Hide' : 'Show'} month-by-month, monthly business costs & top products</button>
          {months && <>
            <MonthlyChart data={data.overview} />
            <MonthlyCostsTable data={data.overview} reload={load} />
            <TopProducts rows={data.overview.top_products} />
          </>}
        </div>
      )}
      {showSales && data && t?.uncosted > 0 && (
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" checked={onlyUncosted} onChange={(e) => setOnlyUncosted(e.target.checked)} data-testid="money-only-uncosted" /> Only sales missing a cost ({t.uncosted})</label>
      )}
      {data && <MoneyTable rows={onlyUncosted ? data.rows.filter((r) => r.type === 'sale' && ((r.editable_cost && (r.manual_cost === null || r.manual_cost === undefined) && !r.cost_complete) || r.missing_costs)) : data.rows} showProfit={showSales} onEdit={(order) => setDialog({ order })} onDelete={remove} reload={load} />}
      <SalesOrderDialog open={!!dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} order={dialog?.order || null} showProfit />
    </div>
  );
}
